import React, { Component, ErrorInfo, ReactNode } from "react"

// Define the props interface for the ErrorBoundary component
interface ErrorBoundaryProps {
  children: ReactNode // The child components that the ErrorBoundary will wrap
  fallback?: ReactNode // Optional fallback UI to display when an error occurs
}

// Define the state interface for the ErrorBoundary component
interface ErrorBoundaryState {
  hasError: boolean // Indicates whether an error has occurred
  error: Error | null // Stores the error object
  errorInfo: ErrorInfo | null // Stores additional error information
}

/**
 * ErrorBoundary Component
 * Catches JavaScript errors in its child component tree, logs them,
 * and displays a fallback UI.
 *
 * Usage:
 * <ErrorBoundary>
 * <YourComponentThatMightThrowErrors />
 * </ErrorBoundary>
 *
 * Or with a custom fallback:
 * <ErrorBoundary fallback={<div>Oops! A custom error message.</div>}>
 * <YourComponentThatMightThrowErrors />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Initialize the state
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  /**
   * getDerivedStateFromError is called after an error has been thrown by a descendant component.
   * It receives the error that was thrown as a parameter.
   * This static method is used to update state so the next render will show the fallback UI.
   * @param error The error that was thrown.
   * @returns An object to update the state.
   */
  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state to indicate an error has occurred
    return { hasError: true, error, errorInfo: null } // errorInfo will be populated in componentDidCatch
  }

  /**
   * componentDidCatch is called after an error has been thrown.
   * This method is used for side effects like logging errors.
   * @param error The error that was thrown.
   * @param errorInfo An object with a componentStack key containing information about which component threw the error.
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can also log the error to an error reporting service here
    console.error("Uncaught error in ErrorBoundary:", error, errorInfo)

    // Update state with errorInfo for potential display or further logging
    this.setState({ errorInfo })
  }

  /**
   * Renders the component.
   * If an error has occurred, it displays the fallback UI or a default message.
   * Otherwise, it renders its children.
   * @returns The rendered ReactNode.
   */
  public render(): ReactNode {
    if (this.state.hasError) {
      // If a fallback UI is provided, render it
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Otherwise, render a default error message
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-red-100 text-red-800 p-4 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold mb-2">
            Oops! Something went wrong.
          </h1>
          <p className="text-lg mb-4">
            We're sorry for the inconvenience. Please try refreshing the page.
          </p>
          {this.state.error && (
            <details className="mt-4 p-2 bg-red-200 rounded-md text-sm w-full max-w-lg overflow-auto">
              <summary className="cursor-pointer font-medium">
                Error Details
              </summary>
              <pre className="whitespace-pre-wrap wrap-break-word mt-2">
                {this.state.error.toString()}
                <br />
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      )
    }

    // If no error, render the children as usual
    return this.props.children
  }
}

export default ErrorBoundary
